/**
 * _vcard.js — builds a vCard 3.0 (.vcf) from a Client Hub "Share a contact" snapshot.
 *
 * HELPER, NOT A ROUTE. Imported by BOTH api/journey-admin.js (attached to the subscriber email)
 * and frontend/src/journey/modules/ContactCardEntry.jsx (the client's "Save to contacts" link), so
 * the card a client downloads and the card in their inbox are the same file. The _priorityModel.js
 * precedent: one implementation, two consumers. Pure, no imports.
 *
 * contact: { name, firstName, lastName, company, title, email, phone, street, city, state, zip, website }
 * org:     { name, phone, email, website, street, city, state, zip }
 * vCard 3.0 is what iOS, macOS, Android and Outlook all import without asking questions. An
 * organization share is TWO cards in one file (the company, then its main contact); every one of
 * those apps imports a multi-card .vcf as separate contacts.
 */

function esc(v) {
  return String(v == null ? '' : v)
    .replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;')
}

function splitName(c) {
  let first = (c.firstName || '').trim()
  let last = (c.lastName || '').trim()
  if (!first && !last && c.name) {
    const parts = String(c.name).trim().split(/\s+/)
    last = parts.length > 1 ? parts.pop() : ''
    first = parts.join(' ')
  }
  return { first, last }
}

export function contactDisplayName(c) {
  if (!c) return ''
  const { first, last } = splitName(c)
  return (c.name || [first, last].filter(Boolean).join(' ') || c.company || '').trim()
}

export function buildVCard(c) {
  if (!c) return ''
  const { first, last } = splitName(c)
  const fn = contactDisplayName(c)
  const lines = ['BEGIN:VCARD', 'VERSION:3.0', `N:${esc(last)};${esc(first)};;;`, `FN:${esc(fn)}`]
  if (c.company) lines.push(`ORG:${esc(c.company)}`)
  if (c.title) lines.push(`TITLE:${esc(c.title)}`)
  if (c.email) lines.push(`EMAIL;TYPE=INTERNET,WORK:${esc(c.email)}`)
  if (c.phone) lines.push(`TEL;TYPE=WORK,VOICE:${esc(c.phone)}`)
  if (c.street || c.city || c.state || c.zip) {
    lines.push(`ADR;TYPE=WORK:;;${esc(c.street)};${esc(c.city)};${esc(c.state)};${esc(c.zip)};`)
  }
  if (c.website) lines.push(`URL:${esc(c.website)}`)
  lines.push('END:VCARD')
  return lines.join('\r\n') + '\r\n'
}

export function buildOrgVCard(o) {
  if (!o || !o.name) return ''
  const lines = ['BEGIN:VCARD', 'VERSION:3.0', 'N:;;;;', `FN:${esc(o.name)}`, `ORG:${esc(o.name)}`,
    'X-ABShowAs:COMPANY']   // Apple Contacts files it as a company, not a person with no name
  if (o.email) lines.push(`EMAIL;TYPE=INTERNET,WORK:${esc(o.email)}`)
  if (o.phone) lines.push(`TEL;TYPE=WORK,VOICE:${esc(o.phone)}`)
  if (o.street || o.city || o.state || o.zip) {
    lines.push(`ADR;TYPE=WORK:;;${esc(o.street)};${esc(o.city)};${esc(o.state)};${esc(o.zip)};`)
  }
  if (o.website) lines.push(`URL:${esc(o.website)}`)
  lines.push('END:VCARD')
  return lines.join('\r\n') + '\r\n'
}

/** The whole share from an entry's data: { kind: 'person'|'org', contact, org }. */
export function isOrgShare(d) { return !!(d && d.kind === 'org' && d.org && d.org.name) }
export function buildShareVCard(d) {
  if (!d) return ''
  if (isOrgShare(d)) {
    const main = d.contact && contactDisplayName(d.contact) ? buildVCard({ ...d.contact, company: d.contact.company || d.org.name }) : ''
    return buildOrgVCard(d.org) + main
  }
  return buildVCard(d.contact)
}
export function shareDisplayName(d) {
  if (!d) return ''
  return isOrgShare(d) ? d.org.name : contactDisplayName(d.contact)
}

export function vcardFilename(c) {
  const base = (typeof c === 'string' ? c : contactDisplayName(c)).replace(/[^\w .-]+/g, '').trim().replace(/\s+/g, '-').replace(/\.+$/, '')
  return (base || 'contact') + '.vcf'
}
